import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Chip, Container, Typography, useTheme } from '@mui/material';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { borderRadius, fontSize } from '@verantos-dev/shared-ui-components/tokens';
import { truncateMiddle } from '@verantos-dev/shared-utils';

import { ChatWindowInputFileError, ChatWindowProps } from './ChatWindowTypes';
import { Button } from '../../mui-wrapper-components/Button/Button';
import { TextField } from '../../mui-wrapper-components/TextField/TextField';

interface ChatWindowInputProps {
  userPrompt: string;
  onUserPromptChange: (value: string) => void;
  onSendMessage: () => void;
  isOwner: boolean;
  isWaitingForResponse: boolean;
  isReady: boolean;
  inputPlaceholder?: string;
  suggestedActions?: ChatWindowProps['suggestedActions'];
  fileUploadConfig?: ChatWindowProps['fileUploadConfig'];
  fileUploadDragDropTexts?: ChatWindowProps['fileUploadDragDropTexts'];
  fileError: ChatWindowInputFileError;
  setFileError: (error: ChatWindowInputFileError) => void;
}

export const ChatWindowInput = ({
  userPrompt,
  onUserPromptChange,
  onSendMessage,
  isOwner,
  isWaitingForResponse,
  isReady,
  inputPlaceholder,
  suggestedActions,
  fileUploadConfig,
  fileUploadDragDropTexts,
  fileError,
  setFileError,
}: ChatWindowInputProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onChange = (_: string, value: string) => {
    onUserPromptChange(value);
  };

  let placeholder: string;
  if (inputPlaceholder) {
    placeholder = inputPlaceholder;
  } else if (!isOwner) {
    placeholder = intl.formatMessage({
      id: 'projects.researchAssistant.chatWindow.inputField.placeholder.isNotOwner',
    });
  } else {
    placeholder = intl.formatMessage({
      id: 'projects.researchAssistant.chatWindow.inputField.placeholder.isOwner',
    });
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // only allow newline if shift or ctrl is pressed with enter
    if (
      isWaitingForResponse &&
      !isReady &&
      event.key === 'Enter' &&
      !(event.shiftKey || event.ctrlKey)
    ) {
      event.preventDefault(); // Prevents a new line from being added
    }

    // send message if enter is pressed without shift or ctrl
    if (
      event.key === 'Enter' &&
      !(event.shiftKey || event.ctrlKey) &&
      !isWaitingForResponse &&
      isReady
    ) {
      event.preventDefault(); // Prevents a new line from being added
      onSendMessage();
    }
  };

  const isSendDisabled = !isOwner || !isReady || isWaitingForResponse;
  const hasAttachment = Boolean(fileUploadConfig);
  const attachmentDisabled = isSendDisabled || Boolean(fileUploadConfig?.isDisabled);

  const handleAttachmentClick = () => {
    if (attachmentDisabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const fileExtension = file?.name.split('.').pop()?.toLowerCase();
    const maxFileSize = (fileUploadConfig?.maxFileSizeMB ?? 10) * 1024 * 1024;
    if (fileExtension && !fileUploadConfig?.accept?.includes(fileExtension)) {
      const invalidFileTypeError =
        fileUploadDragDropTexts?.invalidFileType ??
        `Invalid file type. Allowed types: ${fileUploadConfig?.accept ?? ''}`;
      setFileError(invalidFileTypeError);
    } else if (file && file.size > maxFileSize) {
      const fileTooLargeError =
        fileUploadDragDropTexts?.fileTooLarge ??
        `File size exceeds ${fileUploadConfig?.maxFileSizeMB ?? 10}MB limit`;
      setFileError(fileTooLargeError);
    } else if (file && fileUploadConfig) {
      fileUploadConfig.onFileSelected(file);
    }
    event.target.value = '';
  };

  const attachmentButtonLabel = fileUploadConfig?.buttonLabel ?? (
    <AttachFileIcon fontSize="small" />
  );

  return (
    <>
      {/* Suggested Actions */}
      {suggestedActions && suggestedActions.length > 0 && (
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '16px 0 8px 0 !important',
            margin: '0',
            gap: '8px',
          }}
          maxWidth={false}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: fontSize[14],
              fontWeight: 500,
            }}
          >
            {intl.formatMessage({
              id: 'projects.researchAssistant.suggestedActions.title',
            })}
          </Typography>
          <Container
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '0 !important',
              margin: '0',
            }}
            maxWidth={false}
          >
            {suggestedActions.map((action) => (
              <Button
                key={action.id}
                id={action.id}
                variant="outlined"
                size="small"
                onClickHandler={action.onClick}
                disabled={isSendDisabled}
                displayLabel={truncateMiddle(action.label, 50)}
                sx={{
                  fontSize: fontSize[14],
                  textTransform: 'none',
                  borderRadius: `${borderRadius.xl}px`,
                  padding: '4px 12px',
                  minHeight: '32px',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.white.main,
                    borderColor: theme.palette.primary.light,
                  },
                  '&:disabled': {
                    borderColor: theme.palette.action.disabled,
                    color: theme.palette.action.disabled,
                  },
                }}
              />
            ))}
          </Container>
        </Container>
      )}

      {/* Error alert — inline above input */}
      {fileError && (
        <Alert
          severity="error"
          onClose={() => setFileError(null)}
          sx={{
            width: '100%',
            mb: 1,
          }}
        >
          {fileError}
        </Alert>
      )}

      {/* Input Field */}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '10px 0 !important',
          margin: '0',
          // TODO: no VDS token for this value — needs designer input (10px radius)
          borderRadius: '10px',
          width: '100%',
        }}
        maxWidth={false}
      >
        {hasAttachment && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={fileUploadConfig?.accept}
              style={{ display: 'none' }}
              onChange={handleAttachmentChange}
            />
            <Button
              id="chatInputAttachmentButton"
              variant="outlined"
              size="large"
              aria-label={fileUploadConfig?.buttonAriaLabel ?? 'Attach file'}
              sx={{
                height: '100%',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                minWidth: '48px',
              }}
              displayLabel={attachmentButtonLabel}
              onClickHandler={handleAttachmentClick}
              disabled={attachmentDisabled}
            />
          </>
        )}
        <TextField
          id="chatInputField"
          width={'auto'}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              borderTopLeftRadius: hasAttachment ? 0 : undefined,
              borderBottomLeftRadius: hasAttachment ? 0 : undefined,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
          displayLabel=""
          multiline
          autoFocus={true}
          value={userPrompt}
          placeholder={placeholder}
          readOnly={!isOwner || !isReady}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        <Button
          id="chatInputButton"
          variant="contained"
          color="primary"
          size="large"
          sx={{
            height: '100%',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            boxShadow: 'none', // intentional override — suppresses MUI button elevation
          }}
          displayLabel={<SendIcon />}
          onClickHandler={onSendMessage}
          disabled={isSendDisabled}
        />
      </Container>

      {fileUploadConfig?.selectedFileName && (
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '4px 0 0 0 !important',
            margin: '0',
            gap: 0.5,
          }}
          maxWidth={false}
        >
          <Typography variant="caption" color="text.secondary">
            Attachments ready to send
          </Typography>
          <Chip
            label={fileUploadConfig.selectedFileName}
            onDelete={fileUploadConfig.onClearSelectedFile}
            deleteIcon={
              fileUploadConfig.onClearSelectedFile ? <CloseIcon fontSize="small" /> : undefined
            }
            sx={{
              borderRadius: `${borderRadius.xl}px`,
              // TODO: no VDS token for this value — needs designer input (fontSize 13)
              fontSize: '13px',
              backgroundColor: theme.palette.grey[100],
            }}
          />
        </Container>
      )}
    </>
  );
};
